"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { convertApiDropToExtendedDrop } from "@/helpers/waves/drop.helpers";
import { fetchMemesQuickVoteDrop, getMemesQuickVoteDropQueryKey } from "@/hooks/memesQuickVote.query";
import { useMemesQuickVoteContext } from "@/hooks/useMemesQuickVoteContext";
import { isMemesQuickVoteDiscoverableDrop } from "@/hooks/memesQuickVote.helpers";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
export const useMemesQuickVoteActiveDrop = t0 => {
  const $ = _c(37);
  const {
    activeCandidateId,
    discoveredDropsById,
    enabled,
    nextCandidateId,
    onInvalidatedDrop
  } = t0;
  const queryClient = useQueryClient();
  const {
    contextProfile,
    proxyId
  } = useMemesQuickVoteContext();
  const activeInitialDrop = activeCandidateId ? discoveredDropsById[activeCandidateId] : undefined;
  let t1;
  if ($[0] !== activeCandidateId || $[1] !== contextProfile || $[2] !== proxyId) {
    t1 = activeCandidateId ? getMemesQuickVoteDropQueryKey({
      contextProfile,
      dropId: activeCandidateId,
      proxyId
    }) : [QueryKey.DROP, {
      context: "memes-quick-vote",
      drop_id: null
    }];
    $[0] = activeCandidateId;
    $[1] = contextProfile;
    $[2] = proxyId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== activeCandidateId) {
    t2 = () => fetchMemesQuickVoteDrop(activeCandidateId);
    $[4] = activeCandidateId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = enabled && !!activeCandidateId;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = getDefaultQueryRetry();
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== activeInitialDrop || $[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t5 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      initialData: activeInitialDrop,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      staleTime: 0,
      ...t4
    };
    $[7] = activeInitialDrop;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const activeQuery = useQuery(t5);
  const isUsingInitialData = !!activeInitialDrop && activeQuery.data === activeInitialDrop && !activeQuery.isFetchedAfterMount;
  const hasFreshData = !!activeQuery.data && !isUsingInitialData;
  let t6;
  let t7;
  if ($[12] !== contextProfile || $[13] !== enabled || $[14] !== nextCandidateId || $[15] !== proxyId || $[16] !== queryClient) {
    t6 = () => {
      if (!enabled || !nextCandidateId) {
        return;
      }
      queryClient.prefetchQuery({
        queryKey: getMemesQuickVoteDropQueryKey({
          contextProfile,
          dropId: nextCandidateId,
          proxyId
        }),
        queryFn: () => fetchMemesQuickVoteDrop(nextCandidateId),
        staleTime: 0,
        ...getDefaultQueryRetry()
      });
    };
    t7 = [contextProfile, enabled, nextCandidateId, proxyId, queryClient];
    $[12] = contextProfile;
    $[13] = enabled;
    $[14] = nextCandidateId;
    $[15] = proxyId;
    $[16] = queryClient;
    $[17] = t6;
    $[18] = t7;
  } else {
    t6 = $[17];
    t7 = $[18];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[19] !== activeCandidateId || $[20] !== activeQuery.data || $[21] !== hasFreshData || $[22] !== onInvalidatedDrop) {
    t8 = () => {
      if (!activeCandidateId || !activeQuery.data || !hasFreshData) {
        return;
      }
      if (isMemesQuickVoteDiscoverableDrop({
        drop: activeQuery.data
      })) {
        return;
      }
      onInvalidatedDrop(activeCandidateId);
    };
    t9 = [activeCandidateId, activeQuery.data, hasFreshData, onInvalidatedDrop];
    $[19] = activeCandidateId;
    $[20] = activeQuery.data;
    $[21] = hasFreshData;
    $[22] = onInvalidatedDrop;
    $[23] = t8;
    $[24] = t9;
  } else {
    t8 = $[23];
    t9 = $[24];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[25] !== activeCandidateId || $[26] !== activeQuery.error || $[27] !== activeQuery.isFetching || $[28] !== onInvalidatedDrop) {
    t10 = () => {
      if (!activeCandidateId || activeQuery.isFetching || !activeQuery.error) {
        return;
      }
      onInvalidatedDrop(activeCandidateId);
    };
    t11 = [activeCandidateId, activeQuery.error, activeQuery.isFetching, onInvalidatedDrop];
    $[25] = activeCandidateId;
    $[26] = activeQuery.error;
    $[27] = activeQuery.isFetching;
    $[28] = onInvalidatedDrop;
    $[29] = t10;
    $[30] = t11;
  } else {
    t10 = $[29];
    t11 = $[30];
  }
  useEffect(t10, t11);
  let t12;
  bb0: {
    if (!activeQuery.data) {
      t12 = null;
      break bb0;
    }
    let t13;
    if ($[31] !== activeQuery.data) {
      t13 = convertApiDropToExtendedDrop(activeQuery.data);
      $[31] = activeQuery.data;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    t12 = t13;
  }
  const activeDrop = t12;
  const t13 = !!activeCandidateId && !activeQuery.data && (activeQuery.isLoading || activeQuery.isFetching);
  let t14;
  if ($[33] !== activeDrop || $[34] !== hasFreshData || $[35] !== t13) {
    t14 = {
      activeDrop,
      hasFreshData,
      isLoading: t13
    };
    $[33] = activeDrop;
    $[34] = hasFreshData;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  return t14;
};